// =============================================================================
// Global Custom Elements
// Shared Web Components used across sections/blocks.
// Loaded via theme.liquid (defer, after constants.js).
// Dependencies: constants.js (MIN_DEVICE_WIDTH, BUTTON_HOVER_ANM, SELECT_ELEMENT_EVENT)
//               getValueCssVariable — defined in theme.js global scope
// =============================================================================

// -----------------------------------------------------------------------------
// IntersectionElement — base class for intersection-observer-based elements
// -----------------------------------------------------------------------------
class IntersectionElement extends HTMLElement {
  constructor() {
    super();
    this.isIntersecting = false;
    this.observer = null;
    this.isValidContext = false;
    this.observerElement = this;
  }

  connectedCallback() {
    this.handleInit();

    this.isValidContext = this.checkValidContext();
    if (!this.isValidContext) return;

    this.actionBeforeSetupObserver();
    this.setupObserver();
    this.actionAfterSetupObserver();
  }

  disconnectedCallback() {
    if (!this.isValidContext) return;

    if (this.isIntersecting) {
      this.handleLeave();
      this.isIntersecting = false;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  handleInit() {}

  checkValidContext() {
    return true;
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isIntersecting) {
            this.isIntersecting = true;
            this.handleIntersect();
          }

          if (!entry.isIntersecting && this.isIntersecting) {
            this.isIntersecting = false;
            this.handleLeave();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.01
      }
    );

    this.observer.observe(this.observerElement);
  }

  actionBeforeSetupObserver() {}
  actionAfterSetupObserver() {}
  handleIntersect() {}
  handleLeave() {}
}

// -----------------------------------------------------------------------------
// SelectElement — custom dropdown/select component
// -----------------------------------------------------------------------------
class SelectElement extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector(".select-button");
    this.label = this.querySelector(".select-label");
    this.labelButton = this.querySelector(".select-button__label");
    this.optionsListWrapper = this.querySelector(".select-options-wrapper");
    this.selectType = this.dataset.selectType;
    this.noRequire = this.dataset.noRequire;
    this.arrowDropdown = this.querySelector(".select-arrow");

    this.isOpen = false;
    this.optionSelected = this.querySelector('.select-option[aria-selected="true"]');
    this.focusOption = null;
    this.optionsList = this.querySelector(".select-options");
    this.listOptionsEle = this.querySelectorAll(".select-option");
  }

  connectedCallback() {
    this.button.addEventListener("keydown", this.handleKeydown);
    this.button.addEventListener("click", this.handleClickButton);
    window.addEventListener("scroll", this.handleScroll);
    this.optionsListWrapper.classList.add('h-0');

    this.handleAddEventItem();

    this.initObserver();
  }

  disconnectedCallback() {
    this.button.removeEventListener("keydown", this.handleKeydown);
    this.button.removeEventListener("click", this.handleClickButton);
    window.removeEventListener("scroll", this.handleScroll);

    this.optionsList.removeEventListener('click', this.handleOptionClick);
    this.optionsList.removeEventListener('mouseover', this.handleOptionMouseOver);
    this.optionsList.removeEventListener('mouseout', this.handleOptionMouseOut);
  }

  handleAddEventItem() {
    this.optionsList?.addEventListener('click', this.handleOptionClick);
    this.optionsList?.addEventListener('mouseover', this.handleOptionMouseOver);
    this.optionsList?.addEventListener('mouseout', this.handleOptionMouseOut);
  }

  handleKeydown = (evt) => {
    if (!this.isOpen) {
      if (evt.key === "Enter" || evt.key === "Spacebar" || evt.key === " ") {
        evt.preventDefault();
        this.handleOpen();
      }
    }
  }

  handleKeydownWhenOpened = (evt) => {
    if (evt.key === "Enter" || evt.key === "Spacebar" || evt.key === " ") {
      evt.preventDefault();
      this.handleClose();
    } else if (evt.key === "ArrowDown" || evt.key === "Down") {
      evt.preventDefault();
      this.selectedOptionIndex = 0;
      this.setOptionFocus(this.selectedOptionIndex);
    } else if (evt.key === "ArrowUp" || evt.key === "Up") {
      evt.preventDefault();
      this.selectedOptionIndex = this.listOptionsEle.length - 1;
      this.setOptionFocus(this.selectedOptionIndex);
    } else if (evt.key === "Tab") {
      this.handleClose();
    } else if (evt.key === "Escape" || evt.key === "Esc") {
      evt.preventDefault();
      evt.stopPropagation();
      this.handleClose();
    }
  }

  handleClickButton = (evt) => {
    evt.preventDefault();

    if (this.isOpen) {
      this.handleClose();
    } else {
      this.handleOpen();
    }
    evt.stopPropagation();
  }

  handleOpen() {
    this.listOptionsEle = Array.from(this.querySelectorAll(".select-option"));
    if (this.listOptionsEle.length === 0) return;

    this.optionSelected = this.querySelector('.select-option[aria-selected="true"]');
    this.optionSelectedEle = this.querySelector(".select-option-selected");
    if (!this.optionSelected && this.listOptionsEle.length > 0 && this.noRequire !== "true") {
      this.optionSelected = this.listOptionsEle[0];
    }

    this.button.addEventListener("keydown", this.handleKeydownWhenOpened);

    document.addEventListener("mousedown", this.handleClickBody);

    if (this.arrowDropdown) this.arrowDropdown.classList.add("rotate-180");

    requestAnimationFrame(() => {
      this.optionsListWrapper.classList.remove('h-0');
      this.isOpen = true;
      this.button.ariaExpanded = true;

      if (this.selectType !== "product-picker") {
        this.adjustPosition();
      } else {
        this.optionsListWrapper.classList.add("is-open");
      }

      this.optionsListWrapper.addEventListener("keydown", this.handleKeydownOptionList);
    });
  }

  handleClose() {
    this.isOpen = false;
    this.button.ariaExpanded = false;
    this.optionsListWrapper.classList.remove("is-open");
    if (this.arrowDropdown) this.arrowDropdown.classList.remove("rotate-180");

    if (this.optionSelected) this.optionSelected.classList.add("selected");

    this.optionsListWrapper.removeEventListener("keydown", this.handleKeydownOptionList);
    this.button.removeEventListener("keydown", this.handleKeydownWhenOpened);
    this.listOptionsEle = Array.from(this.listOptionsEle);
    this.listOptionsEle.forEach((opt) => {
      opt.tabIndex = -1;
      opt.classList.remove("focused");
    });

    this.button.focus();
    document.removeEventListener("mousedown", this.handleClickBody);

    setTimeout(() => {
      this.optionsListWrapper.classList.add('h-0');
    }, 100);
  }

  handleClickBody = (evt) => {
    if (!this.isOpen || (evt && evt.target && (this.optionsListWrapper.contains(evt.target) || this.button.contains(evt.target)))) return;

    evt.preventDefault();
    evt.stopPropagation();
    this.handleClose();
    if (evt?.target) {
      evt.target.focus();
    }
  }

  handleScroll = () => {
    if (this.isOpen && this.selectType !== "product-picker") {
      this.handleClose();
    }
  }

  handleOptionMouseOver = (evt) => {
    if (this.focusOption) {
      this.focusOption.classList.remove("focused");
    }

    if (!evt.target.closest(".select-option")) return;
    this.focusOption = evt.target.closest(".select-option");
    this.focusOption.classList.add("focused");
  }

  handleOptionMouseOut = () => {
    if (this.focusOption) {
      this.focusOption.classList.remove("focused");
    }
  }

  handleOptionClick = (event) => {
    const newOptionSelected = event.target.closest(".select-option");
    if (newOptionSelected?.classList.contains("disabled") && this.selectType === "product-picker") return;
    this.setSelectedOption(newOptionSelected);
    this.handleClose(null, true);
  }

  handleKeydownOptionList = (evt) => {
    const key = evt.key;
    if (key === "ArrowDown") {
      evt.preventDefault();
      this.moveFocus(1);
    } else if (key === "ArrowUp") {
      evt.preventDefault();
      this.moveFocus(-1);
    } else if (key === "Escape" || key === "Esc" || key === "Tab") {
      evt.preventDefault();
      evt.stopPropagation();
      this.handleClose();
    } else if (evt.key === "Enter" || evt.key === "Spacebar" || evt.key === " ") {
      evt.preventDefault();
      this.setSelectedOption(evt.target.closest(".select-option"));
      this.handleClose();
    }
  }

  moveFocus(step) {
    const options = Array.from(this.listOptionsEle);
    if (!options.length) return;

    if (this.selectedOptionIndex == null) {
      this.selectedOptionIndex = 0;
    } else if (this.selectedOptionIndex < 0 ) {
      this.selectedOptionIndex = options.length;
    } else {
      this.selectedOptionIndex = (this.selectedOptionIndex + step + options.length) % options.length;
    }

    this.setOptionFocus(this.selectedOptionIndex);

    const currentOption = options[this.selectedOptionIndex];
    if (currentOption) currentOption.scrollIntoView({block: "nearest", inline: "nearest"});
  }

  setOptionFocus(index) {
    const options = Array.from(this.listOptionsEle);
    if (!options.length || index < 0 || index >= options.length) return;

    options.forEach((opt, i) => {
      opt.tabIndex = i === index ? 0 : -1;
      opt.classList.toggle("focused", i === index);
    });

    options[index].focus();
  }

  setSelectedOption(selectedOption) {
    if (this.optionSelected !== selectedOption) {
      if (this.optionSelected) {
        this.optionSelected.ariaSelected = false;
        this.optionSelected.classList.remove('selected');
      }

      selectedOption.ariaSelected = true;
      selectedOption.classList.add('selected');
      let optionSelectedValue = selectedOption.dataset.value;
      this.optionSelected = selectedOption;

      if (this.label) this.label.textContent = optionSelectedValue;
      if (this.labelButton) this.labelButton.textContent = selectedOption.dataset.selectValue;
      if (this.optionSelectedEle) this.optionSelectedEle.setAttribute("value", optionSelectedValue);
      this.dispatchEventChange(selectedOption);
    }
  }

  adjustPosition() {
    if (this.optionsListWrapper.dataset.adjustPosition === "false") {
      this.optionsListWrapper.classList.add("is-open");
    } else {
      const optionListPaddingByPx = 16;

      const { bottom: buttonBottom } = this.button.getBoundingClientRect();

      const optionListHeight = this.optionsListWrapper.getBoundingClientRect().height;

      const dropdownBottom = buttonBottom + optionListHeight + optionListPaddingByPx;
      const showAbove = dropdownBottom > window.innerHeight;

      this.optionsListWrapper.classList.toggle("show-above", showAbove);
      this.optionsListWrapper.classList.add("!transition-none");
      requestAnimationFrame(() => {
        this.optionsListWrapper.classList.remove("!transition-none");
        this.optionsListWrapper.classList.add("is-open");
      })
    }
  }

  initObserver() {
    if (!this.optionsList) return;

    this.observer = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          const target = mutation.target;
          if (target.classList.contains("select-option") && target.classList.contains("selected")) {
            this.optionSelected = target;
            if (this.label) this.label.textContent = target.dataset.value;
            if (this.labelButton) this.labelButton.textContent = target.dataset.selectValue;
          }
        } else if (mutation.type === "childList") {
          this.listOptionsEle = this.querySelectorAll(".select-option");
        }
      }
    });

    this.observer.observe(this.optionsList, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["class"]
    });
  }

  dispatchEventChange(newOptionSelected) {
    newOptionSelected.dispatchEvent(
      new CustomEvent(SELECT_ELEMENT_EVENT.change, {
        bubbles: true,
        cancelable: true,
        detail: {
          target: newOptionSelected,
          value: this.optionSelected.dataset.value,
        },
      })
    );
  }
}

if (!customElements.get("select-element")) {
  customElements.define("select-element", SelectElement);
}

// -----------------------------------------------------------------------------
// CustomToolTip — single-trigger tooltip
// -----------------------------------------------------------------------------
class CustomToolTip extends HTMLElement {
  constructor() {
    super();
    this.tooltipEl = this.querySelector(".tooltip-popup-wrapper");
    this.tooltipTrigger = this.querySelector(".tooltip-trigger");
  }

  connectedCallback() {
    if (this.tooltipEl && this.tooltipTrigger) {
      this.tooltipTrigger.addEventListener("mouseenter", this.handleOpenTooltip.bind(this));
      this.tooltipTrigger.addEventListener("mouseleave", this.handleCloseTooltip.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.tooltipEl && this.tooltipTrigger) {
      this.tooltipTrigger.removeEventListener("mouseenter", this.handleOpenTooltip.bind(this));
      this.tooltipTrigger.removeEventListener("mouseleave", this.handleCloseTooltip.bind(this));
    }
  }

  handleOpenTooltip() {
    if (this.tooltipEl) {
      this.tooltipEl.classList.add("tooltip-visible");
    }
  }

  handleCloseTooltip() {
    if (this.tooltipEl) {
      this.tooltipEl.classList.remove("tooltip-visible");
    }
  }
}

if (!customElements.get("custom-tooltip")) {
  customElements.define("custom-tooltip", CustomToolTip);
}

// -----------------------------------------------------------------------------
// CustomButton — extends IntersectionElement, handles button hover animation
// -----------------------------------------------------------------------------
class CustomButton extends IntersectionElement {
  constructor() {
    super();
    this.domObserver = null;
    this.disabledObserver = null;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.checkDomChange == 'true' && !this.button) {
      this.setupDomObserver();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupDomObserver();
    if (this.disabledObserver) {
      this.disabledObserver.disconnect();
      this.disabledObserver = null;
    }
  }

  setupDomObserver() {
    this.domObserver = new MutationObserver(() => {
      this.handleDomChange();
    });

    this.domObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }

  cleanupDomObserver() {
    if (this.domObserver) {
      this.domObserver.disconnect();
      this.domObserver = null;
    }
  }

  handleDomChange() {
    const newButton = this.querySelector(
      ".button, button, .anm-button-primary, .anm-button-secondary"
    );

    if (!newButton) return;
    this.cleanupDomObserver();
    this.handleInit();

    this.isValidContext = this.checkValidContext();
    if (!this.isValidContext) return;

    this.actionBeforeSetupObserver();
    this.setupObserver();
    this.actionAfterSetupObserver();
  }

  handleInit() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.disabledObserver) {
      this.disabledObserver.disconnect();
      this.disabledObserver = null;
    }
    this.button = this.querySelector(
      ".button, button, .anm-button-primary, .anm-button-secondary"
    );
    this.onMouseEnter = this.handleMouseEnter.bind(this);
    this.onMouseLeave = this.handleMouseLeave.bind(this);
    this.observerElement = this.button;

    // Guard: only needed for thicken mode where `::before` is gated on :not([disabled])
    if (this.button && window.Maximize?.buttonHoverAnm === BUTTON_HOVER_ANM.thicken) {
      this.disabledObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'disabled' && !this.button.disabled) {
            this.button.classList.remove('is-hover');
          }
        }
      });
      this.disabledObserver.observe(this.button, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  checkValidContext() {
    if (!this.button || this.button.classList.contains("button-link")) return false;
    if (this.button.classList.contains('anm-btn-from-context') && !this.button.closest('.anm-btn-controller')) return false;

    if (!window.Maximize || (window.Maximize.buttonHoverAnm != BUTTON_HOVER_ANM.thicken)) return false;

    return true;
  }

  handleIntersect() {
    this.button.addEventListener("mouseenter", this.onMouseEnter);
    this.button.addEventListener("mouseleave", this.onMouseLeave);
  }

  handleLeave() {
    this.button.removeEventListener("mouseenter", this.onMouseEnter);
    this.button.removeEventListener("mouseleave", this.onMouseLeave);
  }

  handleMouseEnter(e) {
    if (window.innerWidth < MIN_DEVICE_WIDTH.desktop) return;

    const rect = this.button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    this.button.classList.remove('is-hover');

    this.button.style.setProperty('--mouse-in-button-x', `${x}px`);
    this.button.style.setProperty('--mouse-in-button-y', `${y}px`);

    this.button.offsetHeight;

    requestAnimationFrame(() => {
      this.button.classList.add('is-hover');
    });
  }

  handleMouseLeave(e) {
    if (window.innerWidth < MIN_DEVICE_WIDTH.desktop) return;

    const rect = this.button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    this.button.style.setProperty('--mouse-in-button-x', `${x}px`);
    this.button.style.setProperty('--mouse-in-button-y', `${y}px`);

    this.button.classList.remove('is-hover');
  }
}

if (!customElements.get("custom-button")) {
  customElements.define("custom-button", CustomButton);
}

// -----------------------------------------------------------------------------
// CustomToolTipContainer — multi-trigger tooltip (updates content on hover)
// Depends on: getValueCssVariable (defined in theme.js global scope)
// -----------------------------------------------------------------------------
class CustomToolTipContainer extends CustomToolTip {
  constructor() {
    super();
    this.listTooltipTrigger = this.querySelectorAll(".container__tooltip-trigger");
  }
  connectedCallback() {
    if (this.tooltipEl && this.listTooltipTrigger.length > 0) {
      this.sectionAnnouncementEl = document.getElementById("x-announcement");
      this.sectionHeaderEl = document.getElementsByClassName("section-header")[0];
      this.listTooltipTrigger.forEach((el) => {
        el.addEventListener("mouseenter", this.handleUpdateTooltip.bind(this, el));
        el.addEventListener("mouseleave", this.handleCloseTooltip.bind(this));
      });
    }
  }

  disconnectedCallback() {
    if (this.tooltipEl && this.listTooltipTrigger.length > 0) {
      this.listTooltipTrigger.forEach((el) => {
        el.removeEventListener("mouseenter", this.handleUpdateTooltip.bind(this, el));
        el.removeEventListener("mouseleave", this.handleCloseTooltip.bind(this));
      });
    }
  }

  handleUpdateTooltip(el) {
    if (this.tooltipEl) {
      const tooltipBoundary = this.tooltipEl.closest(".tooltip-boundary");
      const tooltipContentEl = this.tooltipEl.querySelector(".tooltip-content");

      if (!this.tooltipEl || !tooltipContentEl || el.dataset.isNoText == "true") return;

      this.tooltipEl.dataset.tooltipPosition = "top";

      const temp = document.createElement("div");
      temp.innerHTML = el.innerHTML;
      temp.querySelectorAll('[data-tooltip-exclude]')
        .forEach(el => el.remove());

      tooltipContentEl.innerHTML = temp.innerHTML;

      requestAnimationFrame(() => {
        const tooltipRect = this.tooltipEl.getBoundingClientRect();
        const boundaryRect = tooltipBoundary?.getBoundingClientRect();

        if (tooltipRect.top < (boundaryRect?.top || 0)) {
          this.tooltipEl.dataset.tooltipPosition = "bottom";
        } else {
          let heightHeaderAndAnnouncementBar = 0;
          if (this.sectionAnnouncementEl && this.sectionAnnouncementEl.classList.contains('announcement-sticky')) {
            heightHeaderAndAnnouncementBar += this.sectionAnnouncementEl.getBoundingClientRect().height;
          }

          if (this.sectionHeaderEl && this.sectionHeaderEl.classList.contains('sticky-header') && document.getElementById('x-header-content')) {
            heightHeaderAndAnnouncementBar += getValueCssVariable('--height-header', document.getElementById('x-header-content'), true);
          }

          if (tooltipRect.top < heightHeaderAndAnnouncementBar) {
            this.tooltipEl.dataset.tooltipPosition = "bottom";
          } else {
            this.tooltipEl.dataset.tooltipPosition = "top";
          }
        }
        this.tooltipEl.classList.add("tooltip-visible");
      });
    }
  }
}

if (!customElements.get("custom-tooltip-container")) {
  customElements.define("custom-tooltip-container", CustomToolTipContainer);
}

class SlideElement extends HTMLElement {
  constructor() {
    super();
    this.listItems = Array.from(this.getElementsByClassName("slide-element__item"));

    this.activeIndex = -1;
    this.prevActiveIndex = -1;
    this.animationInterval = null;
    this.classNames = {
      active: "is-active",
      prevActive: "is-prev-active",
      transitionNone: "!transition-none",
    };
    this.isFistTimeVisible = true;
    this.isIntersecting = false;
  }

  connectedCallback() {
    if (this.listItems.length === 0) return;
    this.initSlideElement();
    this.setupObserver();
  }

  disconnectedCallback() {
    this.handleLeave();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.handleIntersect();
          } else {
            this.handleLeave();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.01,
      }
    );

    this.observer.observe(this);
  }

  initSlideElement() {
    let customConfig;
    try {
      customConfig = JSON.parse(this.dataset.config);
      if (typeof customConfig === "string") customConfig = JSON.parse(customConfig);
    } catch {
      customConfig = {};
    }

    customConfig.gap && this.style.setProperty("--slide-element-gap", `${customConfig.gap}px`);
    customConfig.effectDuration && this.style.setProperty("--slide-effect-duration", customConfig.effectDuration);

    this.config = {
      timeToNext: customConfig.timeToNext ? parseInt(customConfig.timeToNext) * MS_PER_SECOND : 0,
      gap: (customConfig.gap ? customConfig.gap : SLIDE_ELEMENT.SLIDE_ELEMENT_GAP) || 0,
      effectDuration: (customConfig.effectDuration ? customConfig.effectDuration : SLIDE_ELEMENT.SLIDER_ELEMENT_DURATION) || 0,
    };
  }

  handleIntersect() {
    if (this.listItems.length === 0) return;

    if (this.isFistTimeVisible) {
      this.activeIndex = 0;
      this.listItems[this.activeIndex]?.classList.add(this.classNames.active, this.classNames.transitionNone);
      requestAnimationFrame(() => {
        this.listItems[this.activeIndex]?.classList.remove(this.classNames.transitionNone);
        this.isFistTimeVisible = false;
      });
    }

    this.handleAutoPlay();
    this.isIntersecting = true;
  }

  handleLeave() {
    this.isIntersecting = false;
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
  }

  handleAutoPlay() {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }

    if (this.animationTimeOut) {
      clearTimeout(this.animationTimeOut);
      this.animationTimeOut = null;
    }

    if (this.listItems.length > 1 && this.config.timeToNext > 0) {
      if (this.isIntersecting) {
        const duration = this.config.timeToNext;
        this.animationInterval = setInterval(() => {
          this.slide(true);
        }, duration);
      } else {
        const duration = this.config.timeToNext - this.config.effectDuration;
        this.animationTimeOut = setTimeout(() => {
          this.slide(true);
          this.handleAutoPlay();
        }, duration);
      }
    }
  }

  slide(next = true) {
    const currentActiveIndex = this.activeIndex;
    const nextActiveItemIndex = next ? (this.activeIndex + 1) % this.listItems.length : (this.activeIndex - 1 + this.listItems.length) % this.listItems.length;

    if (this.prevActiveIndex !== -1) {
      this.listItems[this.prevActiveIndex].classList.remove(this.classNames.prevActive);
    }
    this.listItems[this.activeIndex].classList.remove(this.classNames.active);

    this.prevActiveIndex = currentActiveIndex;

    this.activeIndex = nextActiveItemIndex;

    this.listItems[this.prevActiveIndex].classList.add(this.classNames.prevActive);
    this.listItems[this.activeIndex].classList.add(this.classNames.active);

    setTimeout(() => {
      this.listItems[this.prevActiveIndex]?.classList.remove(this.classNames.prevActive);
    }, this.config.effectDuration);
  }

  nextItem() {
    this.slide(true);
    this.handleAutoPlay();
  }

  backItem() {
    this.slide(false);
    this.handleAutoPlay();
  }
}

if (!customElements.get("slide-element")) {
  customElements.define("slide-element", SlideElement);
}

class CustomCollections extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.currentTab = 1;
    this.isLoading = false;
    this.cachedData = {};
    this.onSelectCollection = this.handleSelectCollection.bind(this);
    this.preventViewAllClick = this.preventViewAllClick.bind(this);
    this.viewAllDisabled = false;
    this._initialized = false;
    this._observer = null;
  }

  connectedCallback() {
    // In design mode init immediately so merchants can interact with all sections
    if (Shopify.designMode) {
      this._init();
      return;
    }

    // Defer heavy init (cloneNode, querySelectorAll, event listeners) until
    // the element is within 200px of the viewport — keeps connectedCallback
    // near-instant and removes its cost from TBT.
    this._observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this._observer = null;
        this._init();
      },
      { rootMargin: "200px 0px" }
    );
    this._observer.observe(this);
  }

  _init() {
    if (this._initialized) return;
    this._initialized = true;

    this.collectionButtons = this.querySelectorAll(".collection-title");
    this.collectionsWrapperEl = document.getElementById(`Collections--${this.sectionId}`);
    this.viewAllLinks = this.querySelectorAll(".collection__view-all");

    if (!this.collectionsWrapperEl) return;

    if (this.collectionButtons.length > 1) {
      const cloneCollectionsWrapperEl = this.collectionsWrapperEl.cloneNode(true);
      this.anmRevealItems = cloneCollectionsWrapperEl.querySelectorAll(".anm-reveal-item");
      this.anmRevealItems.forEach((item) => {
        item.classList.remove("anm-reveal-item");
      });
      this.cachedData[`tabIndex-${this.currentTab}`] = cloneCollectionsWrapperEl.innerHTML;

      this.collectionButtons.forEach((button) => {
        button.addEventListener("click", this.onSelectCollection);
      });
    }

    if (this.collectionButtons[0]) {
      this.selectCollection(1);
    }

    if (this.viewAllLinks.length > 0) {
      this.viewAllLinks.forEach((el) => {
        el.addEventListener("click", this.preventViewAllClick);
      });

      this.setViewAllLink(this.dataset.defaultViewAllLink);
    }
  }

  disconnectedCallback() {
    // Clean up observer if element removed before init fired
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (!this.collectionButtons) return;

    this.collectionButtons.forEach((button) => {
      button.removeEventListener("click", this.onSelectCollection);
    });

    this.viewAllLinks.forEach((el) => {
      el.removeEventListener("click", this.preventViewAllClick);
    });
  }

  async handleSelectCollection(e) {
    if (!this.collectionsWrapperEl) return;

    const tabIndex = parseInt(e.currentTarget.dataset.tabIndex, 10);
    const viewAllLink = e.currentTarget.dataset.viewAllLink;
    if (this.currentTab !== tabIndex && !this.isLoading) {
      await this.loadDataCollection(tabIndex);
      this.selectCollection(tabIndex);
      if (this.viewAllLinks.length > 0) {
        this.setViewAllLink(viewAllLink);
      }
    }
  }

  selectCollection(index) {
    this.collectionButtons.forEach((button) => {
      const tabIndex = parseInt(button.dataset.tabIndex, 10);
      if (tabIndex === index) {
        button.classList.add("is-active");
      } else {
        button.classList.remove("is-active");
      }
    });
  }

  setViewAllLink(link) {
    const isDisabled = !link;

    this.viewAllLinks.forEach((el) => {
      el.href = link || "";
      el.classList.toggle("opacity-70", isDisabled);
      el.classList.toggle("hover:cursor-not-allowed", isDisabled);
    });

    this.viewAllDisabled = isDisabled;
  }

  preventViewAllClick(e) {
    if (this.viewAllDisabled) {
      e.preventDefault();
    }
  }

  selectCollectionButtonsLoading(isLoading, nextTabIndexActive) {
    if (isLoading) {
      this.collectionButtons[nextTabIndexActive - 1].classList.add("next-tab-active");
      this.collectionButtons.forEach((button) => {
        button.setAttribute("aria-disabled", "true");
      });
    } else {
      this.collectionButtons[nextTabIndexActive - 1].classList.remove("next-tab-active");
      this.collectionButtons.forEach((button) => {
        button.removeAttribute("aria-disabled");
      });
    }
    this.isLoading = isLoading;
  }

  async loadDataCollection(index) {
    const tabIndexKey = `tabIndex-${index}`;

    if (this.cachedData[tabIndexKey]) {
      this.collectionsWrapperEl.innerHTML = this.cachedData[tabIndexKey];
      this.currentTab = index;
    } else {
      this.selectCollectionButtonsLoading(true, index);
      let url = `${window.location.pathname}?section_id=${this.sectionId}&page=${index}`;

      try {
        const response = await fetch(url);
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const newContent = html.getElementById(`Collections--${this.sectionId}`);
        if (newContent) {
          this.collectionsWrapperEl.innerHTML = newContent.innerHTML;
          this.anmRevealItems = newContent.querySelectorAll(".anm-reveal-item");
          this.anmRevealItems.forEach((item) => {
            item.classList.remove("anm-reveal-item");
          });
          this.cachedData[tabIndexKey] = newContent.innerHTML;
          this.currentTab = index;
        }
      } catch (error) {
        console.error(error);
      }

      this.selectCollectionButtonsLoading(false, index);
    }
  }
}

if (!customElements.get("custom-collections")) {
  customElements.define("custom-collections", CustomCollections);
}
